import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";

export type SortDir = "asc" | "desc";

/** Bo'sh katak filtr ro'yxatida shunday ko'rinadi. */
export const BLANK = "(bo'sh)";

const collator = new Intl.Collator("uz", { numeric: true, sensitivity: "base" });

/** Raqam, sana va matnni bir xil tartibda solishtiradi. */
export function compareValues(a: unknown, b: unknown): number {
  const empty = (value: unknown) => value === null || value === undefined || value === "";
  if (empty(a) || empty(b)) return empty(a) === empty(b) ? 0 : empty(a) ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return collator.compare(String(a), String(b));
}

/**
 * Ustun menyusi — Excel dagi kabi: saralash va qiymatlar ro'yxati
 * bo'yicha filtr. Belgilangan qiymatlar ko'rinadi, olib tashlangani
 * yashiriladi.
 */
export function ColumnMenu({
  anchor,
  title,
  values,
  selected,
  sort,
  onSort,
  onFilter,
  onClose,
}: {
  anchor: HTMLElement;
  title: string;
  /** Ustundagi barcha turli qiymatlar (filtrdan oldingi). */
  values: string[];
  /** `undefined` — filtr yo'q, hammasi ko'rinadi. */
  selected: Set<string> | undefined;
  sort: SortDir | undefined;
  onSort: (dir: SortDir | undefined) => void;
  onFilter: (next: Set<string> | undefined) => void;
  onClose: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [needle, setNeedle] = useState("");

  const rect = anchor.getBoundingClientRect();
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - 268));
  const top = Math.min(rect.bottom + 4, window.innerHeight - 200);

  useEffect(() => {
    const away = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!box.current?.contains(target) && !anchor.contains(target)) onClose();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
      window.removeEventListener("resize", onClose);
    };
  }, [anchor, onClose]);

  const shown = useMemo(() => {
    const query = needle.trim().toLowerCase();
    return query ? values.filter((value) => value.toLowerCase().includes(query)) : values;
  }, [values, needle]);

  const isOn = (value: string) => !selected || selected.has(value);
  const allOn = shown.every(isOn);

  function toggle(value: string) {
    const next = new Set(selected ?? values);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onFilter(next.size === values.length ? undefined : next);
  }

  function toggleAll() {
    const next = new Set(selected ?? values);
    for (const value of shown) {
      if (allOn) next.delete(value);
      else next.add(value);
    }
    onFilter(next.size === values.length ? undefined : next);
  }

  return createPortal(
    <div ref={box} className="col-menu" style={{ left, top }} role="dialog" aria-label={`${title} filtri`}>
      <div className="col-menu-sort">
        <button
          type="button"
          className={`btn btn-text btn-sm${sort === "asc" ? " on" : ""}`}
          onClick={() => onSort(sort === "asc" ? undefined : "asc")}
        >
          A → Z
        </button>
        <button
          type="button"
          className={`btn btn-text btn-sm${sort === "desc" ? " on" : ""}`}
          onClick={() => onSort(sort === "desc" ? undefined : "desc")}
        >
          Z → A
        </button>
      </div>
      <input
        className="input"
        placeholder="Qidirish…"
        value={needle}
        autoFocus
        onChange={(event) => setNeedle(event.target.value)}
      />
      <div className="col-menu-list">
        {shown.length === 0 ? (
          <label>Mos qiymat yo'q</label>
        ) : (
          <>
            <label className="all">
              <input type="checkbox" checked={allOn} onChange={toggleAll} />
              Hammasini tanlash
            </label>
            {shown.map((value) => (
              <label key={value} title={value}>
                <input type="checkbox" checked={isOn(value)} onChange={() => toggle(value)} />
                {value}
              </label>
            ))}
          </>
        )}
      </div>
      <div className="col-menu-foot">
        <button
          type="button"
          className="btn btn-text btn-sm"
          disabled={!selected && !sort}
          onClick={() => {
            onFilter(undefined);
            onSort(undefined);
          }}
        >
          <Icon name="close" size={14} />
          Tozalash
        </button>
        <button type="button" className="btn btn-tonal btn-sm" onClick={onClose}>
          Yopish
        </button>
      </div>
    </div>,
    document.body,
  );
}
